import { useEffect, useRef } from 'react';
import queryString from 'query-string';
import { useAtom, useSetAtom } from 'jotai';
import * as state from '@/components/Analysis/state';
import * as globalState from '@/state';
import { updateUrl } from '@/utils';
import {
  parseTemplateDateFiltersFromQueryParam,
  serializeAppliedDateFiltersForUrl,
  TEMPLATE_DATE_FILTERS_QUERY_KEY,
} from '@/utils/templateDateFiltersUrl';

export function useFilterSyncWithParms() {
  const [selectedTemplateFilters, setSelectedTemplateFilters] = useAtom(
    state.selectedTemplateFiltersState
  );
  const [selectedDeckIds, setSelectedDeckIds] = useAtom(state.selectedFilteredDeckIds);
  const [selectedWorkpackIds, SetselectedWorkpackIds] = useAtom(state.selectedFilteredWorkpackIds);
  const [selectedPaintRegionIds, setSelectedPaintRegionId] = useAtom(
    state.selectedFilteredPaintRegionIds
  );
  const [selectedStructureDefectsLegends, setSelectedStructureDefectsLegends] = useAtom(
    state.selectedStructureDefectsLegends
  );
  const [enableDynamicPointSize, setEnableDynamicPointSize] = useAtom<boolean>(
    globalState.enableDynamicPointSize
  );
  const [pointSize, setPointSize] = useAtom<number>(globalState.pointSize);
  const [edlStrength, setEdlStrength] = useAtom<number>(globalState.edlStrength);
  const [dynamicPointSize, setDynamicPointSize] = useAtom(globalState.dynamicPointSize);
  const [areSphericalsVisible, setAreSphericalsVisible] = useAtom(state.areSphericalsVisible);
  const [areDecksVisible, setAreDecksVisible] = useAtom(state.areDecksVisible);
  const [showAssemblyCadModel, setShowAssemblyCadModel] = useAtom(state.showAssemblyCadModel);
  const [arePoisVisible, setArePoisVisible] = useAtom(state.arePoisVisible);
  const [showOnlySelectedPoi, setShowOnlySelectedPoi] = useAtom(state.showOnlySelectedPoi);
  const [neighbouringImagesMode, setNeighbouringImagesMode] = useAtom(state.neighbouringImagesMode);
  const [showPointOfInterestMarkerLabels, setShowPointOfInterestMarkerLabels] = useAtom(
    state.showPointOfInterestMarkerLabels
  );
  const [markerLabelScale, setMarkerLabelScale] = useAtom<number>(state.markerLabelScale);
  const [swivelMarkerLabels, setSwivelMarkerLabels] = useAtom<boolean>(state.swivelMarkerLabels);
  const [appliedDateFilters, setAppliedDateFilters] = useAtom(state.appliedDateFilters);
  const setDateFilters = useSetAtom(state.dateFilters);
  const appliedDefectsFromUrl = useRef(false);
  useEffect(() => {
    const query = window.location.search;
    const parsed = queryString.parse(query, { arrayFormat: 'comma' });

    if (parsed.templateFilters) {
      const filters = JSON.parse(decodeURIComponent(parsed.templateFilters.toString()));
      const mappedFilteres = filters.map(({ id, o }: { id: string; o: string[] }) => ({
        id,
        value: [...o],
      }));
      setSelectedTemplateFilters(mappedFilteres);
    }
    const templateDateFiltersRaw = parsed[TEMPLATE_DATE_FILTERS_QUERY_KEY];
    if (templateDateFiltersRaw) {
      const datesFromUrl = parseTemplateDateFiltersFromQueryParam(templateDateFiltersRaw);
      if (datesFromUrl) {
        setDateFilters(datesFromUrl);
        setAppliedDateFilters(datesFromUrl);
      }
    }

    if (parsed.paintRegionFilter) {
      const filter = JSON.parse(decodeURIComponent(parsed.paintRegionFilter.toString()));
      setSelectedPaintRegionId(filter.value ?? []);
    }

    if (parsed.deckFilters) {
      const filter = JSON.parse(decodeURIComponent(parsed.deckFilters.toString()));
      setSelectedDeckIds(filter.value ?? []);
    }
    if (parsed.workPackFilter) {
      const filter = JSON.parse(decodeURIComponent(parsed.workPackFilter.toString()));
      SetselectedWorkpackIds(filter.value ?? []);
    }
    if (parsed.enableDynamicPointSize) {
      const filter = JSON.parse(decodeURIComponent(parsed.enableDynamicPointSize.toString()));
      setEnableDynamicPointSize(filter.value ?? false);
    }

    if (parsed.pointSize) {
      const filter = JSON.parse(decodeURIComponent(parsed.pointSize.toString()));
      setPointSize(filter.value ?? 1);
    }
    if (parsed.enhancedPoints) {
      const filter = JSON.parse(decodeURIComponent(parsed.enhancedPoints.toString()));
      setEdlStrength(filter.value ?? 0.25);
    }
    if (parsed.enableViewpoints) {
      const filter = JSON.parse(decodeURIComponent(parsed.enableViewpoints.toString()));
      setAreSphericalsVisible(filter.value ?? false);
    }
    if (parsed.enableDeckGrids) {
      const filter = JSON.parse(decodeURIComponent(parsed.enableDeckGrids.toString()));
      setAreDecksVisible(filter.value ?? false);
    }
    if (parsed.enableAssemblyCad) {
      const filter = JSON.parse(decodeURIComponent(parsed.enableAssemblyCad.toString()));
      setShowAssemblyCadModel(filter.value ?? false);
    }
    if (parsed.showAllPois) {
      const filter = JSON.parse(decodeURIComponent(parsed.showAllPois.toString()));
      setArePoisVisible(filter.value ?? false);
    }
    if (parsed.showSelectedPoi) {
      const filter = JSON.parse(decodeURIComponent(parsed.showSelectedPoi.toString()));
      setShowOnlySelectedPoi(filter.value ?? false);
    }
    if (parsed.enablePoiAnnotation) {
      const filter = JSON.parse(decodeURIComponent(parsed.enablePoiAnnotation.toString()));
      setShowPointOfInterestMarkerLabels(filter.value ?? false);
    }
    if (parsed.annotationScale) {
      const filter = JSON.parse(decodeURIComponent(parsed.annotationScale.toString()));
      setMarkerLabelScale(filter.value ?? 1);
    }

    if (parsed.annotationOrientation) {
      const filter = JSON.parse(decodeURIComponent(parsed.annotationOrientation.toString()));
      setSwivelMarkerLabels(filter.value ?? false);
    }
    if (parsed.neighbouringImagesMode) {
      const filter = JSON.parse(decodeURIComponent(parsed.neighbouringImagesMode.toString()));
      setNeighbouringImagesMode((current) => ({ ...current, isEnabled: filter.value ?? false }));
    }
    if (parsed.neighbouringImagesFrames) {
      const filter = JSON.parse(decodeURIComponent(parsed.neighbouringImagesFrames.toString()));
      setNeighbouringImagesMode((current) => ({ ...current, showFrames: filter.value ?? false }));
    }
    if (parsed.neighbouringImage) {
      const filter = JSON.parse(decodeURIComponent(parsed.neighbouringImage.toString()));
      setNeighbouringImagesMode((current) => ({ ...current, showImages: filter.value ?? false }));
    }
  }, [
    setNeighbouringImagesMode,
    setSwivelMarkerLabels,
    setMarkerLabelScale,
    setShowPointOfInterestMarkerLabels,
    setShowOnlySelectedPoi,
    setArePoisVisible,
    setShowAssemblyCadModel,
    setAreDecksVisible,
    setAreSphericalsVisible,
    setEdlStrength,
    setPointSize,
    setEnableDynamicPointSize,
    setDynamicPointSize,
    setSelectedStructureDefectsLegends,
    setSelectedPaintRegionId,
    setSelectedTemplateFilters,
    setSelectedDeckIds,
    SetselectedWorkpackIds,
    setDateFilters,
    setAppliedDateFilters,
  ]);

  useEffect(() => {
    if (appliedDefectsFromUrl.current) return;

    const query = window.location.search;
    const parsed = queryString.parse(query, { arrayFormat: 'comma' });

    if (parsed.defectsLengends && Array.isArray(selectedStructureDefectsLegends)) {
      try {
        const parsedDefects = JSON.parse(decodeURIComponent(String(parsed.defectsLengends)));
        const rawIds = Array.isArray(parsedDefects?.value) ? parsedDefects.value : [];
        const rawIdsStrings = rawIds.map(String);

        setSelectedStructureDefectsLegends((current) =>
          Array.isArray(current)
            ? current.map((d) => ({
                ...d,
                isEnabled: rawIdsStrings.includes(String(d.id)),
              }))
            : current
        );

        appliedDefectsFromUrl.current = true;
      } catch (e) {
        console.error('Failed to parse defect legends from URL:', e);
      }
    }
  }, [selectedStructureDefectsLegends, setSelectedStructureDefectsLegends]);

  useEffect(() => {
    if (!selectedStructureDefectsLegends || !Array.isArray(selectedStructureDefectsLegends)) {
      return undefined;
    }
    const selectedDefect = selectedStructureDefectsLegends
      ?.filter((defect) => defect.isEnabled === true)
      .map((defect) => defect.id);
    const filters = [
      {
        key: 'defectsLengends',
        has: () => selectedDefect && selectedDefect.length > 0,
        value: () => encodeURIComponent(JSON.stringify({ value: selectedDefect })),
      },
    ];
    for (const { key, has, value } of filters) {
      updateUrl(key, has() ? value() : undefined);
    }
  }, [selectedStructureDefectsLegends]);

  useEffect(() => {
    const filters = [
      {
        key: 'templateFilters',
        has: () => selectedTemplateFilters.length > 0,
        value: () =>
          encodeURIComponent(
            JSON.stringify(selectedTemplateFilters.map((f) => ({ id: f.id, o: [...f.value] })))
          ),
      },
      {
        key: TEMPLATE_DATE_FILTERS_QUERY_KEY,
        has: () => Boolean(serializeAppliedDateFiltersForUrl(appliedDateFilters)),
        value: () => serializeAppliedDateFiltersForUrl(appliedDateFilters) as string,
      },
      {
        key: 'paintRegionFilter',
        has: () => selectedPaintRegionIds.length > 0,
        value: () =>
          encodeURIComponent(
            JSON.stringify({ id: 'paintRegionIds', value: selectedPaintRegionIds })
          ),
      },
      {
        key: 'deckFilters',
        has: () => selectedDeckIds.length > 0,
        value: () =>
          encodeURIComponent(JSON.stringify({ id: 'selectedDeckIds', value: selectedDeckIds })),
      },
      {
        key: 'workPackFilter',
        has: () => selectedWorkpackIds.length > 0,
        value: () =>
          encodeURIComponent(JSON.stringify({ id: 'workpackIds', value: selectedWorkpackIds })),
      },
      {
        key: 'enableDynamicPointSize',
        has: () => enableDynamicPointSize,
        value: () => encodeURIComponent(JSON.stringify({ value: enableDynamicPointSize })),
      },

      {
        key: 'pointSize',
        has: () => !enableDynamicPointSize,
        value: () => encodeURIComponent(JSON.stringify({ value: pointSize })),
      },

      {
        key: 'enhancedPoints',
        has: () => true,
        value: () => encodeURIComponent(JSON.stringify({ value: edlStrength })),
      },
      {
        key: 'enableViewpoints',
        has: () => areSphericalsVisible,
        value: () => encodeURIComponent(JSON.stringify({ value: areSphericalsVisible })),
      },
      {
        key: 'enableDeckGrids',
        has: () => areDecksVisible,
        value: () => encodeURIComponent(JSON.stringify({ value: areDecksVisible })),
      },
      {
        key: 'enableAssemblyCad',
        has: () => showAssemblyCadModel,
        value: () => encodeURIComponent(JSON.stringify({ value: showAssemblyCadModel })),
      },
      {
        key: 'showAllPois',
        has: () => arePoisVisible,
        value: () => encodeURIComponent(JSON.stringify({ value: arePoisVisible })),
      },
      {
        key: 'showSelectedPoi',
        has: () => showOnlySelectedPoi,
        value: () => encodeURIComponent(JSON.stringify({ value: showOnlySelectedPoi })),
      },
      {
        key: 'enablePoiAnnotation',
        has: () => showPointOfInterestMarkerLabels,
        value: () => encodeURIComponent(JSON.stringify({ value: showPointOfInterestMarkerLabels })),
      },
      {
        key: 'annotationScale',
        has: () => showPointOfInterestMarkerLabels,
        value: () => encodeURIComponent(JSON.stringify({ value: markerLabelScale })),
      },
      {
        key: 'annotationOrientation',
        has: () => showPointOfInterestMarkerLabels,
        value: () => encodeURIComponent(JSON.stringify({ value: swivelMarkerLabels })),
      },
      {
        key: 'neighbouringImagesMode',
        has: () => neighbouringImagesMode.isEnabled,
        value: () =>
          encodeURIComponent(JSON.stringify({ value: neighbouringImagesMode.isEnabled })),
      },
      {
        key: 'neighbouringImagesFrames',
        has: () => neighbouringImagesMode.showFrames,
        value: () =>
          encodeURIComponent(JSON.stringify({ value: neighbouringImagesMode.showFrames })),
      },
      {
        key: 'neighbouringImage',
        has: () => neighbouringImagesMode.showImages,
        value: () =>
          encodeURIComponent(JSON.stringify({ value: neighbouringImagesMode.showImages })),
      },
    ];
    for (const { key, has, value } of filters) {
      updateUrl(key, has() ? value() : undefined);
    }
  }, [
    neighbouringImagesMode,
    swivelMarkerLabels,
    markerLabelScale,
    showPointOfInterestMarkerLabels,
    arePoisVisible,
    showOnlySelectedPoi,
    showAssemblyCadModel,
    areDecksVisible,
    areSphericalsVisible,
    edlStrength,
    pointSize,
    dynamicPointSize,
    enableDynamicPointSize,
    selectedTemplateFilters,
    selectedPaintRegionIds,
    selectedDeckIds,
    selectedWorkpackIds,
    appliedDateFilters,
  ]);
}
